'use client';

import React from 'react';
import type { PlacesType } from 'react-tooltip';
import TextareaAutosize from 'react-textarea-autosize';
import { Icon } from "@plura/client/components";
import cn from "classnames";
import Tooltip from '../../atoms/Tooltip';
import { useFormHelper } from '@plura/client/utils';
import '../_shared/conversation-form-molecules.css';


type TextAreaProps = React.InputHTMLAttributes<HTMLTextAreaElement> &
{
  label?: string;
  error?: string;
  className?: string;
  classLabel?: string;
  classInput?: string;
  icon?: string;
  copy?: boolean;
  tooltip?: string;
  place?: PlacesType;
  footNote?: string;
  rows?: number;
  withResize?: boolean;
  onChangeOverride?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
};

const textareaClass = cn(
  'w-full min-h-[120px] max-h-[350px] rounded-xl border-2 border-transparent bg-[#f4f4f4] p-2.5 font-[Inter,sans-serif] text-[15px] font-semibold leading-[24px] text-[#1a1d1f] transition-all duration-200',
  '[.dark-mode_&]:border-[#272b30] [.dark-mode_&]:bg-[#272b30] [.dark-mode_&]:text-[#fcfcfc]',
  'placeholder:text-[#6f767e] [.dark-mode_&]:placeholder:text-[#9a9fa5]',
  'focus:border-[#9a9fa5] focus:bg-white focus:outline-none [.dark-mode_&]:focus:border-[#33383f] [.dark-mode_&]:focus:bg-[#111315]',
  'disabled:text-[#6f767e] [.dark-mode_&]:disabled:text-[#9a9fa5]',
  '[&:-webkit-autofill]:[transition:background-color_100000s_ease-in-out_0s]',
);

const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>((props, ref) => {
  const {
    label,
    className,
    classLabel,
    classInput,
    icon,
    copy,
    tooltip,
    place,
    footNote,
    rows,
    withResize,
    onKeyDown,
    onChangeOverride,
    ...rest
  } = props;

  const { onValueChange, errorMessage, ...formHelperProps } = useFormHelper(rest);

  const onChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (onChangeOverride) {
      onChangeOverride(e);
    } else {
      formHelperProps.onChange(e);
    }
  }

  return (
    <div className={cn('w-full', className)}>
      {label && (
        <div className={cn(classLabel, 'label-item mb-3.5 text-sm font-semibold leading-[24px] text-[#33383f] [.dark-mode_&]:text-[#efefef]')}>
          {label}{" "}
          {tooltip && (
            <Tooltip
              className="relative -top-px [&_svg]:fill-[#9a9fa5]"
              content={tooltip}
              place={place ? place : "right"}
            />
          )}
        </div>
      )}
      <div className={cn('plura-field-wrap', !!errorMessage && 'plura-field-wrap--error')}>
        {
          withResize
            ? <TextareaAutosize
              ref={ref}
              {...formHelperProps}
              placeholder={props.placeholder}
              onChange={onChange}
              className={cn(
                textareaClass,
                icon && 'pl-12',
                (copy || errorMessage) && 'pr-12',
                !!errorMessage && 'min-h-[50px] border-transparent bg-[rgba(255,188,153,0.25)] text-[#ff6a55] placeholder:text-[#ff6a55] [.dark-mode_&]:focus:border-[#ff6a55]',
                classInput,
              )}
              data-gramm="false"
              data-gramm_editor="false"
              data-enable-grammarly="false"
            />
            : <textarea
              ref={ref}
              {...rest}
              {...formHelperProps}
              className={cn(
                textareaClass,
                icon && 'pl-12',
                (copy || errorMessage) && 'pr-12',
                !!errorMessage && 'min-h-[50px] border-transparent bg-[rgba(255,188,153,0.25)] text-[#ff6a55] placeholder:text-[#ff6a55] [.dark-mode_&]:focus:border-[#ff6a55]',
                classInput,
              )}
              data-gramm="false"
              data-gramm_editor="false"
              data-enable-grammarly="false"
            />
        }
        {icon && (
          <div
            className={cn(
              'pointer-events-none absolute bottom-0 left-0 top-0 flex h-12 w-12 items-center justify-center',
              errorMessage ? '[&_i]:text-[#ff6a55]' : '[&_i]:text-[#6f767e]',
            )}
          >
            <i className={icon} />{" "}
          </div>
        )}
        {copy && (
          <button type="button" className="absolute bottom-0 right-0 top-0 flex h-12 w-12 items-center justify-center [&_svg]:fill-[#6f767e] transition-[fill] duration-200 hover:[&_svg]:fill-[#2a85ff]">
            <Icon name="copy" className="!size-6" />{" "}
          </button>
        )}

        {
          errorMessage &&
          <p className="mt-1 text-right text-sm text-[#ff6a55]">{errorMessage}</p>
        }

        {footNote && <p className="-mt-1.5 text-right text-xs text-[#6f767e] [.dark-mode_&]:text-[#efefef]">{footNote}</p>}
      </div>
    </div>
  );
});

TextArea.displayName = 'TextArea';

export default TextArea
